import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { formatMoney } from 'lib/functions/utils';
import { Document } from 'lib/types/models';

interface PaymentDocumentProps {
  document?: Document;
}

export default function PaymentDocument({ document }: PaymentDocumentProps) {
  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <DescriptionIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Documento de Pago
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Chip
              label={'Prima de Garantía'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: '18px', fontSize: '0.65rem' }}
            />
            <Chip
              label={document?.paidAt ? 'Pagado' : 'Pendiente'}
              size="small"
              color={document?.paidAt ? 'success' : 'warning'}
              sx={{ height: '18px', fontSize: '0.65rem' }}
            />
          </Stack>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Stack
            direction="row"
            alignItems="start"
            justifyContent="flex-end"
            spacing={0.75}
          >
            <Typography variant="h4" color="primary">
              {formatMoney(document?.amount || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CLP
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <Box
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 1,
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Folio:
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {document?.folio}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Fecha de Emisión:
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {new Date(document?.issuedDate || '').toLocaleDateString('es-CL')}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Empresa:
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {document?.stakeholderIdentifier}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Alert
        severity="info"
        icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
        sx={{
          borderRadius: 0.5,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.2)',
          py: 0.75,
          mt: 2,
          '& .MuiAlert-message': { py: 0.25 },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          Información Importante
        </AlertTitle>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
        >
          El pago debe realizarse mediante transferencia bancaria. Una vez
          completado el pago, es necesario notificar a la empresa
          correspondiente para proceder con la conciliación y la emisión del
          certificado correspondiente.
        </Typography>
      </Alert>
    </Paper>
  );
}
